import { defineStore } from "pinia";
import { computed, ref } from "vue";
import type { AuthenticatedUser, SSORole } from "../../data/datasources/authRemoteDataSource";
import { AuthRepository } from "../../data/repositories/authRepository";

const TOKEN_KEY = "access_token";

export const useAuthStore = defineStore("auth", () => {
  const accessToken = ref<string | null>(localStorage.getItem(TOKEN_KEY));
  const profile = ref<AuthenticatedUser | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  let fetchPromise: Promise<void> | null = null;

  const isAuthenticated = computed(() => !!accessToken.value);
  const isProfileLoaded = computed(() => !!profile.value);

  const role = computed<SSORole | null>(() => profile.value?.role ?? null);
  const permissions = computed<unknown[]>(() => {
    const perms = profile.value?.role?.permissions;
    return Array.isArray(perms) ? perms : [];
  });

  function hasPermission(permissionKey: string): boolean {
    return permissions.value.some((p: any) => p === permissionKey || p?.key === permissionKey);
  }

  function setToken(token: string): void {
    accessToken.value = token;
    localStorage.setItem(TOKEN_KEY, token);
  }

  async function fetchProfile(): Promise<void> {
    if (fetchPromise) return fetchPromise;

    isLoading.value = true;
    error.value = null;

    fetchPromise = (async () => {
      try {
        const repo = new AuthRepository();
        const response = await repo.getProfile();
        profile.value = response.data;
      } catch (err) {
        error.value = "Failed to fetch profile";
        profile.value = null;
        throw err;
      } finally {
        isLoading.value = false;
        fetchPromise = null;
      }
    })();

    return fetchPromise;
  }

  function clearProfile(): void {
    profile.value = null;
  }

  function logout(): void {
    accessToken.value = null;
    profile.value = null;
    error.value = null;
    localStorage.removeItem(TOKEN_KEY);
  }

  return {
    accessToken,
    profile,
    isLoading,
    error,
    isAuthenticated,
    isProfileLoaded,
    role,
    permissions,
    hasPermission,
    setToken,
    fetchProfile,
    clearProfile,
    logout,
  };
});
